/* @flow */
import React from "react";

import { lowerCase } from "./stringUtil";
import { FavouriteData } from "../stores/ArticleSuggestionStore";
import {
    ArticleSuggestionProperties,
    RecentSearchesSuggestion,
    SuggestionWithProperties
} from "../datatypes/ArticleSuggestions";
import { loadRecentSearches } from "../components/articles/shared/RecentSearches";
import { isNil, words } from "shared/src/utils/util";

export function normalizeQuery(query: string | undefined | null): string {
    return words(lowerCase(query)).join(" ");
}

export function findSuggestions<T>(
    value: string,
    map: {
        [x: string]: T;
    }
): T {
    let v = value;
    let result = map[v];
    while (isNil(result) && v.length > 0) {
        v = v.slice(0, -1);
        result = map[v];
    }
    return result;
}

export function wrapValues(
    suggestions: Array<string>,
    suggestionsProperties: Array<ArticleSuggestionProperties> | undefined | null
): Array<SuggestionWithProperties> {
    const resultArray: SuggestionWithProperties[] = [];
    for (let i = 0; i < suggestions.length; i++) {
        const properties = suggestionsProperties && suggestionsProperties.length > i ? suggestionsProperties[i] : null;
        const className = properties && properties.categoryId ? "icon-small-link" : "icon-small-magnify";
        resultArray.push({
            type: "SUGGESTION",
            value: suggestions[i],
            originalValue: suggestions[i],
            properties: properties,
            icon: { className: className }
        });
    }
    const categorySuggestions = resultArray.filter(
        (e) => e.properties && e.properties.isCategory && e.properties.categoryId !== null
    );

    const brandSuggestions = resultArray.filter(
        (e) => e.properties && e.properties.isBrand && e.properties.categoryId === null
    );
    const contentSuggestions = resultArray.filter((e) => e.properties && e.properties.contentUrl !== null);
    const otherSuggestions = resultArray.filter(
        (e) => !e.properties || (!e.properties.isCategory && !e.properties.isBrand && e.properties.contentUrl === null)
    );
    return categorySuggestions.concat(brandSuggestions, contentSuggestions, otherSuggestions);
}

export function wrapFavourites(
    favourites: Array<string>,
    data: {
        [x: string]: FavouriteData;
    }
): Array<{
    type: "FAVOURITE";
    data: FavouriteData;
}> {
    return favourites
        .map((variantId) => ({ type: "FAVOURITE" as const, data: data[variantId] }))
        .filter((suggestion) => !!suggestion.data);
}

export function findRecentSearchSuggestions(query: string): Array<RecentSearchesSuggestion> {
    const recentSearchSuggestions = loadRecentSearches();
    const matchesRecentSearch = recentSearchSuggestions
        .filter((element) => {
            const recentSearchSuggestion = element.recentSearches.query.split(" ");
            const startWith = recentSearchSuggestion.some((item) => item.startsWith(lowerCase(query)));
            const includes = element.recentSearches.query.includes(lowerCase(query));
            if (startWith && includes) {
                return element;
            }
            return null;
        })
        .slice(0, 3);
    return matchesRecentSearch;
}

export function computeHighlighting(query: string, suggestion: string, noMatchBold: boolean): [string, boolean][] {
    const splitSuggestion: string[] = suggestion.trim().split(/\s+/);
    const splitQuery: string[] = query.trim().split(/\s+/);
    const result: [string, boolean][] = [];
    splitSuggestion.forEach((suggestionWord) => {
        let longestMatch: string = "";
        let longestMatchStartIdx: number = -1;
        splitQuery.forEach((queryWord) => {
            const matchIdx: number = lowerCase(suggestionWord).indexOf(lowerCase(queryWord));
            // Prefer longer matches.
            if (matchIdx !== -1 && queryWord.length > longestMatch.length) {
                longestMatch = queryWord;
                longestMatchStartIdx = matchIdx;
            }
        });
        if (longestMatchStartIdx === -1) {
            result.push([suggestionWord, noMatchBold]);
        } else {
            // The prefix is bold, the match is normal and the suffix is bold.
            if (longestMatchStartIdx !== 0) {
                result.push([suggestionWord.slice(0, longestMatchStartIdx), true]);
            }
            const suffixStartIdx = longestMatchStartIdx + longestMatch.length;
            result.push([suggestionWord.slice(longestMatchStartIdx, suffixStartIdx), false]);
            if (suffixStartIdx < suggestionWord.length) {
                result.push([suggestionWord.slice(suffixStartIdx), true]);
            }
        }
        result.push([" ", false]);
    });
    result.pop();
    return result;
}

export function formatHighlighting(highlighting: [string, boolean][]): JSX.Element[] {
    return highlighting.map((tuple, i: number) => (
        <React.Fragment key={i}>{tuple[1] ? <b>{tuple[0]}</b> : tuple[0]}</React.Fragment>
    ));
}
